import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useTRPCAPI, useTRPCMutation } from '../../../../trpc/trpc';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { isFollowLimitError } from './isFollowLimitError';
import { demoReadOnlyMessage, M24_DEMO_READ_ONLY, parseM24LimitError } from '../../utils/parseM24LimitError';
import { showToast } from '../../../../redux/slices/toasts/slice';
import { ToastType } from '../../../../redux/slices/toasts/state';
import { browseFollowMessages } from '../browseMessages';

export type BrowseFollowKind = 'artist' | 'track' | 'playlist';

/**
 * Follow and unfollow from a Browse row (design a1: Follow on every row). After a change it reads
 * the search and the usage again, so the cost chip says "Followed" and the follow slots move. A
 * full follow slot opens the plan prompt (design b7, FOLLOW_LIMIT_EXCEEDED). A write to the demo
 * artist shows the read-only toast, and every other failure an error toast, so a click never fails
 * with no word.
 */
export function useBrowseFollow() {
  const trpc = useTRPCAPI();
  const queryClient = useQueryClient();
  const openUpgrade = useUpgradePrompt();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const followArtist = useTRPCMutation(trpc.artists.follow.mutationOptions());
  const unfollowArtist = useTRPCMutation(trpc.artists.unfollow.mutationOptions());
  const followTrack = useTRPCMutation(trpc.tracks.follow.mutationOptions());
  const unfollowTrack = useTRPCMutation(trpc.tracks.unfollow.mutationOptions());
  const followPlaylist = useTRPCMutation(trpc.playlists.follow.mutationOptions());
  const unfollowPlaylist = useTRPCMutation(trpc.playlists.unfollow.mutationOptions());

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.browse.search.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.browse.usage.queryKey() }),
    ]);
  }, [queryClient, trpc.browse.search, trpc.browse.usage]);

  const run = useCallback(
    async (kind: BrowseFollowKind, id: string, shouldFollow: boolean) => {
      setPendingId(id);
      try {
        if (kind === 'artist' && shouldFollow) {
          await followArtist.mutateAsync({ artistId: id });
        } else if (kind === 'artist') {
          await unfollowArtist.mutateAsync({ artistId: id });
        } else if (kind === 'track' && shouldFollow) {
          await followTrack.mutateAsync({ trackId: id });
        } else if (kind === 'track') {
          await unfollowTrack.mutateAsync({ trackId: id });
        } else if (shouldFollow) {
          await followPlaylist.mutateAsync({ playlistId: id });
        } else {
          await unfollowPlaylist.mutateAsync({ playlistId: id });
        }
        await refresh();
      } catch (error) {
        if (isFollowLimitError(error)) {
          openUpgrade(SMLUpgradeFeature.followLimit);
          return;
        }
        const demoMessage = demoReadOnlyMessage(error);
        if (demoMessage !== null) {
          // The same toast as the global mutation handler; the unique id shows it once.
          dispatch(
            showToast({ type: ToastType.info, description: demoMessage, autoHide: true, uniqueId: M24_DEMO_READ_ONLY })
          );
          return;
        }
        if (parseM24LimitError(error) !== null) {
          // A plan limit: the global mutation handler opens the plan prompt.
          return;
        }
        console.error('Browse follow failed', error);
        dispatch(
          showToast({
            type: ToastType.danger,
            description: intl.formatMessage(browseFollowMessages.failed),
            autoHide: true,
            uniqueId: 'browse-follow-failed',
          })
        );
      } finally {
        setPendingId(null);
      }
    },
    [
      followArtist,
      unfollowArtist,
      followTrack,
      unfollowTrack,
      followPlaylist,
      unfollowPlaylist,
      refresh,
      openUpgrade,
      dispatch,
      intl,
    ]
  );

  const follow = useCallback((kind: BrowseFollowKind, id: string) => run(kind, id, true), [run]);
  const unfollow = useCallback((kind: BrowseFollowKind, id: string) => run(kind, id, false), [run]);

  return { follow, unfollow, pendingId };
}
